import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  MOCK_RESERVAS_SOF,
  MOCK_STATUS_SOF,
  MOCK_CONCILIACAO_SOF,
  MOCK_RELATORIO_FISCAL_CONSOLIDADO,
  MOCK_RELATORIO_FISCAL_CREDENCIADO,
  MOCK_HISTORICO_EXPORTACAO_FISCAL,
} from '../dados-simulados';
import type { ReservaSofImportada } from '../sof/importarReservasSof';
import type { ExportacaoFiscalHistoricoLinha, ReservaSofLinha } from '../types';

/** FIN-031.1: Reservas SOF. */
export function useReservasSof(mes?: string) {
  return useQuery({
    queryKey: ['financeiro', 'sof', 'reservas', mes ?? null],
    queryFn: async () => {
      let filtrados = [...MOCK_RESERVAS_SOF];
      if (mes) filtrados = filtrados.filter((r) => r.competencia.startsWith(mes));
      return filtrados;
    },
  });
}

/** Importa planilha de reservas SOF: grava 1 linha por (reserva, competência),
 * atualizando quem já existir — mesma semântica do protótipo. */
export function useImportarReservasSof() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      competencia,
      linhas,
    }: {
      competencia: string;
      linhas: ReservaSofImportada[];
    }) => {
      await new Promise((resolve) => setTimeout(resolve, 500));
      const agora = new Date().toISOString();
      let gravadas = 0;
      for (const linha of linhas) {
        const existente = MOCK_RESERVAS_SOF.find(
          (r) => r.codigoReserva === linha.codigoReserva && r.competencia === competencia,
        );
        if (existente) {
          Object.assign(existente, linha, { ultimaSincronizacao: agora });
        } else {
          const nova: ReservaSofLinha = {
            id: `reserva-${linha.codigoReserva}-${competencia}`,
            competencia,
            ultimaSincronizacao: agora,
            ...linha,
          };
          MOCK_RESERVAS_SOF.push(nova);
        }
        gravadas += 1;
      }
      return { gravadas };
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['financeiro', 'sof', 'reservas'] }),
  });
}

export function useSincronizarReservasSof() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await new Promise((resolve) => setTimeout(resolve, 800));
      return { ok: true as const };
    },
    onSettled: () =>
      void queryClient.invalidateQueries({ queryKey: ['financeiro', 'sof', 'reservas'] }),
  });
}

/** FIN-031.2: Status SOF — painel read-only do ciclo empenho → liquidação → pagamento. */
export function useStatusSof(mes?: string) {
  return useQuery({
    queryKey: ['financeiro', 'sof', 'status', mes ?? null],
    queryFn: async () => {
      let filtrados = [...MOCK_STATUS_SOF];
      if (mes) filtrados = filtrados.filter((s) => s.mesReferencia.startsWith(mes));
      return filtrados;
    },
  });
}

/** Confirma o pagamento de uma medição monitorada no Status SOF — muta o
 * estágio para PAGA (mesma ação de `ConfirmarPagamentoModal` do protótipo,
 * sem o formulário de dados bancários por não termos essa tabela ainda). */
export function useConfirmarPagamentoStatusSof() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (medicaoId: string) => {
      await new Promise((resolve) => setTimeout(resolve, 500));
      const linha = MOCK_STATUS_SOF.find((s) => s.medicaoId === medicaoId);
      if (linha) {
        linha.estagioSof = 'PAGA';
        linha.pagavel = false;
      }
      return { ok: true as const };
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['financeiro', 'sof', 'status'] }),
  });
}

/** FIN-031.4: Conciliação SOF — cruza medição × soma de empenhos pagos. */
export function useConciliacaoSof(mes?: string) {
  return useQuery({
    queryKey: ['financeiro', 'sof', 'conciliacao', mes ?? null],
    queryFn: async () => [...MOCK_CONCILIACAO_SOF],
  });
}

/** FIN-031.5: Relatórios fiscais — consolidado por DRE e por credenciado. */
export function useRelatorioFiscalConsolidado(mes?: string, dreId?: string) {
  return useQuery({
    queryKey: ['financeiro', 'sof', 'relatorios', 'consolidado', mes ?? null, dreId ?? null],
    queryFn: async () => {
      let filtrados = [...MOCK_RELATORIO_FISCAL_CONSOLIDADO];
      if (dreId) filtrados = filtrados.filter((r) => r.dreId === dreId);
      return filtrados;
    },
  });
}

export function useRelatorioFiscalCredenciado(mes?: string, dreId?: string) {
  return useQuery({
    queryKey: ['financeiro', 'sof', 'relatorios', 'credenciados', mes ?? null, dreId ?? null],
    queryFn: async () => {
      let filtrados = [...MOCK_RELATORIO_FISCAL_CREDENCIADO];
      if (dreId) filtrados = filtrados.filter((r) => r.dreId === dreId);
      return filtrados;
    },
  });
}

/** FIN-031.6: Histórico de exportações fiscais (CSV/XLSX de auditoria, PDF definitivo com protocolo SEI). */
export function useHistoricoExportacaoFiscal() {
  return useQuery({
    queryKey: ['financeiro', 'sof', 'relatorios', 'historico'],
    queryFn: async () => [...MOCK_HISTORICO_EXPORTACAO_FISCAL],
  });
}

/** Registra 1 linha no histórico a cada exportação de Relatórios fiscais —
 * mesmo acoplamento do protótipo (`registrar.mutateAsync` em `handleExport`). */
/** SHA-256 do conteúdo exportado, via Web Crypto — sem lib nova. Só PDF
 * gera protocolo definitivo, então só PDF ganha hash (CSV/XLSX são
 * pré-visualização, descartáveis, sem registro imutável). */
async function calcularHashSha256(conteudo: string): Promise<string> {
  const bytes = new TextEncoder().encode(conteudo);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function useRegistrarExportacaoFiscal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dados: {
      tipo: string;
      formato: 'CSV' | 'XLSX' | 'PDF';
      competencia: string;
      registros: number;
      valor: number;
    }) => {
      const numero = `EXP-${new Date().getFullYear()}-${String(MOCK_HISTORICO_EXPORTACAO_FISCAL.length + 1).padStart(4, '0')}`;
      const hashSha256 =
        dados.formato === 'PDF'
          ? await calcularHashSha256(JSON.stringify({ ...dados, numero }))
          : null;
      const linha: ExportacaoFiscalHistoricoLinha = {
        id: numero,
        numero,
        tipo: dados.tipo,
        formato: dados.formato,
        competencia: dados.competencia,
        registros: dados.registros,
        valor: dados.valor.toFixed(2),
        protocoloSei: dados.formato === 'PDF' ? `SEI-${Date.now().toString().slice(-8)}` : null,
        hashSha256,
        geradoEm: new Date().toISOString(),
      };
      MOCK_HISTORICO_EXPORTACAO_FISCAL.unshift(linha);
      return linha;
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({
        queryKey: ['financeiro', 'sof', 'relatorios', 'historico'],
      }),
  });
}
